import { imageAssetUrl } from "@/utils/url";
import { format } from "date-fns";
import { useLoaderData } from "@tanstack/react-router";
import { useMemo } from "react";
import { Route } from "@/routes/(auth)/(headless)/reports/clients/$id.order-history";
import { formatShort } from "creso-core/utils";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import { ReportHeader } from "../components/report-header";
import { ReportContainer } from "../components/report-container";
import { ReportFooter } from "../components/report-footer";
import { DistributorProfileSummary } from "../components/distributor-profile-summary";
import { EmployeePortfolioSummary } from "../components/employee-portfolio-summary";
import { OrdersReport } from "../components/orders-report";

export function OrderHistoryReport() {
  const {
    distributorBranding,
    distributorProfile,
    client,
    employee,
    reportData,
  } = useLoaderData({
    from: "/(auth)/(headless)/reports/clients/$id/order-history",
  });

  const { start_date, end_date } = Route.useSearch();
  const summary = useMemo(() => reportData?.summary ?? null, [reportData]);

  const transactions = useMemo(
    () => reportData?.transactions ?? [],
    [reportData]
  );

  return (
    <>
      <ReportHeader
        distributorBranding={distributorBranding}
        profile={distributorProfile}
      />
      <ReportContainer>
        <div className="content px-8 w-full">
          <div className="flex h-[calc(100vh-150px)] flex-row">
            <div className="flex flex-col flex-1 h-full pt-15 pb-4 justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-semibold">{client?.name}</span>
                <span className="text-sm text-muted-foreground">
                  {client.pan} | {client.phone_number}
                  {client.holding_pattern &&
                  JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
                    ? " | Joint"
                    : null}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-4xl">Order History</span>
                {start_date && end_date ? (
                  <span className="text-lg font-semibold">
                    ({format(start_date, "dd MMM yyyy")} -{" "}
                    {format(end_date, "dd MMM yyyy")})
                  </span>
                ) : null}
                <span className="text-sm text-muted-foreground">
                  Generated on {format(new Date(), "dd MMM yyyy")}
                </span>
              </div>
              {employee ? (
                <EmployeePortfolioSummary
                  employee={employee}
                  distributorProfile={distributorProfile}
                />
              ) : (
                <DistributorProfileSummary
                  distributorProfile={distributorProfile}
                />
              )}
            </div>
            <div className="flex-1 p-15 relative pb-0 bg-linear-to-b from-slate-200 to-slate-50 dark:from-slate-800 dark:to-slate-900 h-full rounded-lg items-center flex flex-col">
              <span className="text-[22px] font-semibold">
                Track your portfolio daily on Creso App
              </span>
              <div className="w-full h-[75%] absolute bottom-0">
                <img
                  src={imageAssetUrl("download-app.webp")}
                  alt="client-order-history"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-5">
            <h4 className="break-before-page mt-15 print:mt-0">
              {start_date && end_date
                ? `Transaction Summary (${format(start_date, "dd MMM yyyy")} - ${format(end_date, "dd MMM yyyy")})`
                : "Transaction Summary"}
            </h4>
            <div className="flex flex-row gap-15 bg-muted rounded-lg px-6 py-4 w-fit">
              <div className="flex flex-col gap-2 items-start">
                <span className="text-sm text-muted-foreground">
                  Total invested
                </span>
                <span className="text-base font-semibold">
                  {formatShort(summary?.total_invested ?? 0)}
                </span>
              </div>
              <div className="flex flex-col gap-2 items-start">
                <span className="text-sm text-muted-foreground">
                  Total redeemed
                </span>
                <span className="text-base font-semibold">
                  {formatShort(summary?.total_redeemed ?? 0)}
                </span>
              </div>
              <div className="flex flex-col gap-2 items-start">
                <span className="text-sm text-muted-foreground">
                  Total switched
                </span>
                <span className="text-base font-semibold">
                  {formatShort(summary?.total_switched ?? 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <OrdersReport
              transactions={transactions}
              startDate={start_date}
              endDate={end_date}
            />
          </div>
        </div>
      </ReportContainer>
      <ReportFooter>{client.name}&apos;s order history report</ReportFooter>
    </>
  );
}
